import * as React from "react"
import { Skeleton } from "@/shared/ui/atoms/Skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/atoms/Table"
import { useIsMobile } from "@/shared/hooks/useMediaQuery"

interface TableSkeletonProps {
  hasCategoryColumn?: boolean
  hasAmountColumn?: boolean
  rowCount?: number
}

export const TableSkeleton = React.memo(function TableSkeleton({
  hasCategoryColumn = true,
  hasAmountColumn = true,
  rowCount = 5,
}: TableSkeletonProps) {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <div className="flex flex-col divide-y divide-border/60 p-1">
        {Array.from({ length: Math.min(rowCount, 4) }).map((_, i) => (
          <div key={i} className="flex flex-col gap-2.5 p-3.5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-col gap-1.5 flex-1">
                <Skeleton className="h-4 w-3/4" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
              {hasAmountColumn && <Skeleton className="h-5 w-20 shrink-0" />}
            </div>
            <div className="flex items-center justify-end gap-2 pt-1 border-t border-border/40">
              <Skeleton className="h-6 w-14 rounded-sm" />
              <Skeleton className="h-6 w-14 rounded-sm" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="relative overflow-x-auto">
      <Table className="cartera-table">
        <TableHeader className="bg-card">
          <TableRow>
            <TableHead className="w-[150px]">Fecha</TableHead>
            <TableHead>Concepto</TableHead>
            {hasCategoryColumn ? <TableHead>Categoría</TableHead> : null}
            <TableHead>Cuenta</TableHead>
            {hasAmountColumn ? <TableHead className="text-right">Importe</TableHead> : null}
            <TableHead className="w-[90px] text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rowCount }).map((_, i) => (
            <TableRow key={i} className="cartera-table-row">
              {/* Fecha */}
              <TableCell className="w-[150px]">
                <Skeleton className="h-3.5 w-24" />
              </TableCell>

              {/* Concepto */}
              <TableCell>
                <Skeleton
                  className="h-4"
                  style={{ width: `${Math.max(40, ((i * 17) % 50) + 45)}%` }}
                />
              </TableCell>

              {/* Categoría */}
              {hasCategoryColumn ? (
                <TableCell>
                  <Skeleton className="h-5 w-20 rounded-full" />
                </TableCell>
              ) : null}

              {/* Cuenta */}
              <TableCell>
                <Skeleton className="h-3.5 w-28" />
              </TableCell>

              {/* Importe */}
              {hasAmountColumn ? (
                <TableCell className="text-right">
                  <Skeleton className="h-4 w-20 ml-auto" />
                </TableCell>
              ) : null}

              {/* Acciones */}
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Skeleton className="size-7 rounded-md" />
                  <Skeleton className="size-7 rounded-md" />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
})

export default TableSkeleton
